<template>
<div v-if="hasContent" class="link-preview" :class="{ 'is-revealed': isRevealed }">
    <transition name="fade">
        <div
            v-show="!isRevealed"
            @click="isDefaultOrManuallyRevealed = true"
            class="reveal-preview">
            Click to see link preview
        </div>
    </transition>
    <a
        class="preview-box"
        :href="linkPreview.url"
        target="_blank">
        <img
            v-if="thumbnailUrl && !thumbnailErr"
            :src="thumbnailUrl"
            @error="thumbnailErr = true"
            alt="thumbnail"
            class="thumbnail"/>
        <div class="preview-body" :class="previewBodyClass">
            {{ previewText }}
        </div>
    </a>
</div>
</template>

<script>
// Add domains as you see fit.
const trustedRootDomains = new Set([
    'twitch.tv', 'betterttv.com', 'frankerfacez.com'
])

export default {
    name: 'LinkPreview',
    props: {
        linkPreview: Object,
        revealLinkPreview: Boolean,
    },
    data: vm => ({
        isDefaultOrManuallyRevealed: vm.revealLinkPreview,
        thumbnailErr: false,
    }),
    computed: {
        isRevealed: vm => vm.isDefaultOrManuallyRevealed || vm.isTrustedDomain,
        hasContent: vm => (vm.thumbnailUrl && !vm.thumbnailErr) || !!vm.previewText,
        favicon: vm => vm.linkPreview.favicons[0],
        // One liners should be flex centered, multi liners shouldn't be. Otherwise
        // the middle of the multi line text is shown, instead of the start.
        previewBodyClass: vm => ({ 'is-multiline': vm.previewText.length >= 60 }),
        previewText: vm => {
            const { siteName, title } = vm.linkPreview
            let text = ''
            if (siteName && title) {
                text = title.includes(siteName)
                    ? title : `${siteName} - ${title}`
            } else if (siteName && !title) {
                text = siteName
            } else if (!siteName && title) {
                text = title
            } else {
                text = vm.domain
            }
            return text
        },
        thumbnailUrl: vm => {
            const lp = vm.linkPreview
            const fallback = vm.favicon || null

            let url = ''
            if (vm.thumbnailErr) {
                url = fallback
            } else if (lp.contentType.includes('html')) {
                url = lp.images[0]
            } else if (lp.mediaType === 'image') {
                url = lp.url
            } else {
                url = fallback
            }
            return url || fallback
        },
        domain: vm => {
            try {
                const { hostname } = new URL(vm.linkPreview.url)
                return hostname.replace(/^(www\.)/, '')
            } catch {
                return null
            }
        },
        isTrustedDomain: vm => {
            // I don't care about SLD's like .co.uk
            const rootDomain = vm.domain.split('.').slice(-2).join('.')
            return trustedRootDomains.has(rootDomain)
        }
    },
    watch: {
        // Facilitate the "live preview" feature on the config page.
        revealLinkPreview () {
            this.isDefaultOrManuallyRevealed = this.revealLinkPreview
        }
    }
}
</script>

<style lang="scss" scoped>
$thumbnail-size: 40px;
$padding-vert: 4px;
// + 1 at end fixes the top of the third line being barely visible.
$line-height: (($thumbnail-size - $padding-vert * 2) / 2) + 1;

.link-preview {
    position: relative;
    font-size: 0.75rem;
    border-radius: 4px;
    overflow: hidden;
    &.is-revealed .preview-box {
        filter: blur(0px);
    }
}

.reveal-preview {
    @include flex-center-children;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    background-color: rgba(0,0,0,0.4);
    color: $offwhite;
    font-size: 1rem;
    cursor: pointer;
}

.preview-box {
    display: flex;
    background: var(--bg-color-lighter);
    // border... yay or nay?
    //border: 1px solid var(--bg-color-lighter);
    max-height: $thumbnail-size;
    filter: blur(4px);
    .thumbnail {
        object-fit: cover;
        flex: 0 0 $thumbnail-size;
        height: $thumbnail-size;
        width: $thumbnail-size;
    }
    .preview-body {
        flex: 1;
        display: flex;
        align-items: center;
        padding: $padding-vert 6px;
        line-height: $line-height;
        &.is-multiline {
            align-items: flex-start;
        }
    }
}
</style>
